/**
 * MCP Write Tools
 *
 * Tools for modifying IDF model data. All modifications are tracked
 * and can be undone. Changes are applied to the in-memory document
 * and synced to the code editor.
 */

import type { MCPTool } from '../types';

/**
 * Set a field value
 */
const setFieldValue: MCPTool = {
  name: 'set_field_value',
  description: `Set the value of a specific field in an object. The value will be validated against the schema. Returns the updated object.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId: {
        type: 'string',
        description: 'The object ID to modify',
      },
      fieldIndex: {
        type: 'number',
        description: 'The 0-based field index',
      },
      value: {
        type: 'string',
        description: 'The new value for the field',
      },
    },
    required: ['objectId', 'fieldIndex', 'value'],
  },
};

/**
 * Set multiple fields at once
 */
const setMultipleFields: MCPTool = {
  name: 'set_multiple_fields',
  description: `Set multiple field values in a single object. More efficient than calling set_field_value multiple times.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId: {
        type: 'string',
        description: 'The object ID to modify',
      },
      fields: {
        type: 'array',
        description: 'Array of field updates',
        items: {
          type: 'object',
          properties: {
            fieldIndex: {
              type: 'number',
              description: 'The 0-based field index',
            },
            value: {
              type: 'string',
              description: 'The new value',
            },
          },
        },
      },
    },
    required: ['objectId', 'fields'],
  },
};

/**
 * Add a new object
 */
const addObject: MCPTool = {
  name: 'add_object',
  description: `Add a new object to the model. Requires the class name and field values. Use get_object_schema first to understand required fields.`,
  inputSchema: {
    type: 'object',
    properties: {
      className: {
        type: 'string',
        description: 'The EnergyPlus object class name (e.g., "Zone", "Material")',
      },
      fields: {
        type: 'array',
        description: 'Array of field values in order. Use empty string for optional fields.',
        items: {
          type: 'string',
        },
      },
      insertAfterObjectId: {
        type: 'string',
        description: 'Optional: insert after this object ID. If not specified, adds at the end.',
      },
    },
    required: ['className', 'fields'],
  },
};

/**
 * Remove an object
 */
const removeObject: MCPTool = {
  name: 'remove_object',
  description: `Remove an object from the model. WARNING: This may break references from other objects. Consider using find_references first.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId: {
        type: 'string',
        description: 'The object ID to remove',
      },
    },
    required: ['objectId'],
  },
};

/**
 * Duplicate an object
 */
const duplicateObject: MCPTool = {
  name: 'duplicate_object',
  description: `Create a copy of an existing object. The name field (if present) will have "_copy" appended. Returns the new object ID.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId: {
        type: 'string',
        description: 'The object ID to duplicate',
      },
      newName: {
        type: 'string',
        description: 'Optional: custom name for the duplicate',
      },
    },
    required: ['objectId'],
  },
};

/**
 * Rename an object
 */
const renameObject: MCPTool = {
  name: 'rename_object',
  description: `Rename an object by updating its name field (field 0). This also updates references to this object in other objects.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId: {
        type: 'string',
        description: 'The object ID to rename',
      },
      newName: {
        type: 'string',
        description: 'The new name for the object',
      },
      updateReferences: {
        type: 'boolean',
        description: 'Whether to update references in other objects (default: true)',
        default: true,
      },
    },
    required: ['objectId', 'newName'],
  },
};

/**
 * Batch update objects
 */
const batchUpdate: MCPTool = {
  name: 'batch_update',
  description: `Apply multiple updates to multiple objects in a single operation. Useful for bulk changes like updating all zones or materials.`,
  inputSchema: {
    type: 'object',
    properties: {
      updates: {
        type: 'array',
        description: 'Array of update operations',
        items: {
          type: 'object',
          properties: {
            objectId: {
              type: 'string',
              description: 'The object ID',
            },
            fieldIndex: {
              type: 'number',
              description: 'The field index to update',
            },
            value: {
              type: 'string',
              description: 'The new value',
            },
          },
        },
      },
    },
    required: ['updates'],
  },
};

/**
 * Apply auto-fix
 */
const applyAutoFix: MCPTool = {
  name: 'apply_auto_fix',
  description: `Apply a pre-defined auto-fix operation to resolve a validation issue. Use get_available_auto_fixes to see available fixes.`,
  inputSchema: {
    type: 'object',
    properties: {
      fixId: {
        type: 'string',
        description: 'The auto-fix operation ID',
      },
    },
    required: ['fixId'],
  },
};

/**
 * Create object from template
 */
const createFromTemplate: MCPTool = {
  name: 'create_from_template',
  description: `Create a new object from a predefined template with sensible defaults. Available templates include common HVAC systems, construction assemblies, and schedules.`,
  inputSchema: {
    type: 'object',
    properties: {
      templateName: {
        type: 'string',
        description: 'The template name (e.g., "simple_wall_construction", "office_occupancy_schedule")',
      },
      customizations: {
        type: 'object',
        description: 'Optional field customizations to override template defaults',
      },
    },
    required: ['templateName'],
  },
};

export const writeTools: MCPTool[] = [
  setFieldValue,
  setMultipleFields,
  addObject,
  removeObject,
  duplicateObject,
  renameObject,
  batchUpdate,
  applyAutoFix,
  createFromTemplate,
];
